import React, { FC, MouseEvent, useCallback, useMemo, useState } from 'react';

import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { ExternalIncentiveModal } from 'pages/loyalty/loyalty-incentives-components/external-incentive-modal';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { OfferLocked } from 'pages/loyalty/offer-locked';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { routes } from 'utils/routing';

import {
  ButtonContainer,
  ButtonsWrapper,
  ErrorMessage,
  LoadingWrapper,
  Padding,
  RewardDescription,
  RewardDetailsContainer,
  RewardImageWrapper,
  RewardName,
  RewardSubtitle,
  StyledActionButton,
  StyledIncentiveImage,
  StyledSecondaryActionButton,
  TierRequirementTag,
  TierRequirementTagLocked,
  TierRequirementText,
  TierRequirementTextLocked,
  Wrapper,
  WrapperButton,
} from './external-incentive-details.styled';
import { IExternalIncentiveDetailsViewProps } from './types';

export const ExternalIncentiveDetailsView: FC<IExternalIncentiveDetailsViewProps> = ({
  id,
  title,
  subtitle,
  image,
  description,
  buttonsConfiguration,
  errorMessage,
  offersInCooldown = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
  isOfferClosed,
  externalOfferModal,
  offerTier,
  offerRequiresAuthentication,
}) => {
  const { setOriginLocation } = useAuthContext();
  const { formatMessage } = useIntl();
  const { navigate, location } = useLocationContext();
  const { isAuthenticated } = useAuthContext();
  const [modalVisibility, setModalVisibility] = useState(false);
  const cdp = useCdpContext();

  const { getContentForTier } = useFeaturesLoyaltyTiersContent();

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const pathName = location.pathname;
  const incentiveDetails = {
    title,
    subtitle,
    description,
    image,
    externalOfferModal,
    buttonsConfiguration,
  };

  const modalDetails = {
    title,
    sanityId: id,
    buttonsConfiguration,
    externalOfferModal,
    image,
  };

  const hasSecondaryButton =
    buttonsConfiguration?.secondButton?.buttonPath &&
    buttonsConfiguration?.secondButton?.buttonText;

  const handleFavoriteClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
        Name: 'Favorite',
        Path: pathName,
      });

      event.preventDefault();
      event.stopPropagation();
      handleToggleFavorite(id, title);
    },
    [handleToggleFavorite, id, title, cdp, pathName]
  );

  const onChangeVisibility = useCallback(() => {
    if (!modalVisibility) {
      cdp.trackEvent({
        name: CustomEventNames.EXTERNAL_OFFER_ADDED_TO_ORDER,
        type: EventTypes.Other,
        attributes: {
          sanityId: id,
          name: title,
        },
      });
    }
    setModalVisibility(!modalVisibility);
  }, [modalVisibility, cdp]);

  const handleTermsAndConditionsClick = () => {
    const termsAndConditonsUrl = buttonsConfiguration?.secondButton?.buttonPath?.locale;

    if (!termsAndConditonsUrl) {
      return;
    }

    navigate(termsAndConditonsUrl);
  };

  const routeToSignUp = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setOriginLocation(pathName);
    navigate(routes.signUp);
  };

  const offerTierName = useMemo(() => {
    let tierName: LoyaltyTierKey;

    // if there is no tier available in the offer, set Tier_1 as default
    if (!offerTier) {
      tierName = LoyaltyTierKey.TIER_1;
    } else {
      tierName = offerTier;
    }

    // if the offer has the initial tier required we don't want to show the tier name badge
    // unless the offer is locked
    if (tierName === LoyaltyTierKey.TIER_1 && !isOfferClosed) {
      return '';
    }

    return getContentForTier(tierName)?.loyaltyTiersUIConfigName?.locale;
  }, [offerTier, getContentForTier, isOfferClosed]);

  const offerIsNotAvailableToRedeem = offerRequiresAuthentication && !isAuthenticated();

  return (
    <Wrapper>
      <RewardImageWrapper>
        {enableFavoriteOffers && isAuthenticated() && (
          <WrapperButton>
            <ButtonForIconFav
              data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
              onClick={handleFavoriteClick}
              aria-label={formatMessage({
                id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
              })}
              isTransparent={false}
            >
              {favoriteLoading ? (
                <LoadingWrapper>
                  <FavoriteLoading />
                </LoadingWrapper>
              ) : (
                <FavoriteIcon isFavorite={favorited} />
              )}
            </ButtonForIconFav>
          </WrapperButton>
        )}

        {incentiveDetails.image && (
          <StyledIncentiveImage
            alt={title ?? ''}
            image={incentiveDetails.image.locale}
            objectFitContain
            isOfferClosed={isAuthenticated() && isOfferClosed}
          />
        )}
        {isAuthenticated() && isOfferClosed && <OfferLocked />}
      </RewardImageWrapper>
      {isAuthenticated() && isOfferClosed && (
        <TierRequirementTagLocked>
          <IconLoyaltyTiersLock />
          <TierRequirementTextLocked>{offerTierName}</TierRequirementTextLocked>
        </TierRequirementTagLocked>
      )}
      {isAuthenticated() && !isOfferClosed && offerTierName && (
        <TierRequirementTag>
          <TierRequirementText>{offerTierName}</TierRequirementText>
        </TierRequirementTag>
      )}
      <RewardDetailsContainer>
        {incentiveDetails.title && <RewardName>{incentiveDetails.title}</RewardName>}
        {incentiveDetails.subtitle && (
          <RewardSubtitle>{incentiveDetails.subtitle.locale}</RewardSubtitle>
        )}
        {incentiveDetails.description && (
          <RewardDescription content={incentiveDetails?.description} />
        )}
        {/* padding element needed for safari / iOS native devices */}
        <Padding />
      </RewardDetailsContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <ButtonsWrapper>
        <ButtonContainer>
          {!isOfferClosed && !offerIsNotAvailableToRedeem && (
            <>
              <StyledActionButton
                onClick={onChangeVisibility}
                data-testid="redeem-external-offer-btn"
                eventAttributes={{
                  Name: 'Mobile Order',
                  Path: pathName,
                }}
              >
                {buttonsConfiguration?.firstButton?.buttonText?.locale}
              </StyledActionButton>

              {hasSecondaryButton && (
                <StyledSecondaryActionButton
                  onClick={handleTermsAndConditionsClick}
                  variant={ActionButtonVariants.TEXT_ONLY}
                  data-testid="terms-and-conditions-external-offer-btn"
                  eventAttributes={{
                    Name: 'Terms and Conditions',
                    Path: pathName,
                  }}
                >
                  {buttonsConfiguration?.secondButton?.buttonText?.locale}
                </StyledSecondaryActionButton>
              )}
            </>
          )}
          {isOfferClosed && !offerIsNotAvailableToRedeem && (
            <StyledActionButton
              data-testid="order-more"
              to={routes.menu}
              onClick={() => navigate(routes.menu)}
            >
              {formatMessage({ id: 'orderMoreToBecomeNextTier' }, { tier: offerTierName })}
            </StyledActionButton>
          )}
          {offerIsNotAvailableToRedeem && (
            <StyledActionButton
              data-testid="sign-up-to-redeem"
              to={routes.signUp}
              onClick={routeToSignUp}
            >
              {formatMessage({ id: 'signUpToAccessOffer' })}
            </StyledActionButton>
          )}
        </ButtonContainer>
      </ButtonsWrapper>
      {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
      {modalVisibility && (
        <ExternalIncentiveModal
          onChangeVisibility={onChangeVisibility}
          modalVisibility={modalVisibility}
          modalDetails={modalDetails}
        />
      )}
    </Wrapper>
  );
};
